interface SkeletonProps {
  className?: string
}

/**
 * Bloque de carga (skeleton). Un rectangulo con el color de superficie elevada
 * que "late" suavemente mientras llega el dato real. Sustituye al spinner de
 * `Cargando` en las vistas cuya forma ya conocemos: el usuario ve la silueta de
 * lo que va a aparecer, no un giro generico, y la pagina no da un salto de layout.
 *
 * Accesibilidad: es puramente decorativo (`aria-hidden`). El contenedor que lo
 * usa debe llevar `role="status"` + `aria-label` para anunciar la carga una vez.
 * `animate-pulse` se desactiva solo bajo `prefers-reduced-motion` (index.css).
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse rounded-md bg-elevado ${className}`}
    />
  )
}

/**
 * Skeleton de una tabla/lista paginada (Logistica, Avisos). Reproduce la doble
 * presentacion real: filas de tabla en escritorio, tarjetas apiladas en movil,
 * para que la silueta coincida con lo que llega y no haya salto de layout.
 */
export function SkeletonList({ rows = 8 }: { rows?: number }) {
  const items = Array.from({ length: rows })
  return (
    <div role="status" aria-label="Cargando listado" aria-live="polite" className="flex flex-col gap-4">
      {/* Tabla en escritorio. */}
      <div className="hidden overflow-hidden rounded-xl border border-borde lg:block">
        <div className="flex items-center gap-4 border-b border-borde bg-elevado px-4 py-3">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="ml-auto h-3 w-16" />
        </div>
        {items.map((_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-borde px-4 py-3 last:border-b-0">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="ml-auto h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>

      {/* Tarjetas en movil. */}
      <ul className="flex flex-col gap-2 lg:hidden">
        {items.map((_, i) => (
          <li key={i} className="flex flex-col gap-2 rounded-xl border border-borde bg-superficie p-4">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-6 w-14 rounded-full" />
            </div>
            <Skeleton className="h-3 w-24" />
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Skeleton de la lista de conversaciones (columna de sesiones: avatar-punto +
 * dos lineas de texto por fila).
 */
export function SkeletonConversations({ rows = 7 }: { rows?: number }) {
  const items = Array.from({ length: rows })
  return (
    <ul role="status" aria-label="Cargando conversaciones" aria-live="polite" className="flex flex-col gap-2">
      {items.map((_, i) => (
        <li key={i} className="flex items-center gap-3 rounded-lg border border-borde bg-superficie p-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-full max-w-56" />
          </div>
        </li>
      ))}
    </ul>
  )
}
